'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Button,
  Skeleton,
  ScrollShadow,
  Avatar,
} from '@heroui/react';
import { FaPaperPlane } from 'react-icons/fa';
import { BsStars } from 'react-icons/bs';
import { SessionType } from '@/util/types/globalTypes';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AttikIcon from '../svg/AttikIcon';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AIModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuestion: string;
  session: SessionType;
}

export default function AIModal({
  isOpen,
  onClose,
  initialQuestion,
  session,
}: AIModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Generate session ID based on user ID
  useEffect(() => {
    if (session?.user?._id) {
      setSessionId(`${session.user._id}-${Date.now()}`);
    }
  }, [session]);

  // Handle initial question when modal opens
  useEffect(() => {
    if (isOpen && initialQuestion && messages.length === 0) {
      handleSendMessage(initialQuestion);
    }
  }, [isOpen, initialQuestion]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setMessages([]);
      setInputValue('');
      setIsStreaming(false);
    }
  }, [isOpen]);

  const handleSendMessage = async (message: string) => {
    if (!message.trim() || isStreaming) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: message.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsStreaming(true);

    // Create assistant message placeholder
    const assistantMessageId = `assistant-${Date.now()}`;
    const assistantMessage: Message = {
      id: assistantMessageId,
      type: 'assistant',
      content: '',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, assistantMessage]);

    try {
      // Abort previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      const response = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionId,
        },
        body: JSON.stringify({
          message: message.trim(),
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            if (data === '[DONE]') {
              break;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.accumulated) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, content: parsed.accumulated }
                      : msg
                  )
                );
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // Request was aborted, don't show error
        return;
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? {
                ...msg,
                content:
                  'Sorry, I encountered an error while processing your request. Please try again.',
              }
            : msg
        )
      );
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage(inputValue);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='4xl'
      scrollBehavior='inside'
      className='overflow-hidden border bg-base-100/50 backdrop-blur-md'
      classNames={{
        base: 'sm:!ml-24 max-h-[90vh]',
        wrapper: 'z-[1000]',
        body: 'p-0',
      }}
      backdrop='opaque'
    >
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1 px-6 pb-4 pt-6'>
          <div className='flex items-start gap-2'>
            <div className='h-8 w-8'>
              <AttikIcon />
            </div>
            <h3 className='font-bold'>AI</h3>
          </div>
          <p className='text-sm font-normal text-gray-500'>
            Ask me anything about your business
          </p>
        </ModalHeader>

        <ModalBody className='px-6'>
          <ScrollShadow className='max-h-[60vh]' hideScrollBar>
            <div className='flex flex-col gap-4 p-2'>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.type === 'assistant' && (
                    <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/30'>
                      <BsStars className='text-primary' size={14} />
                    </div>
                  )}

                  <div
                    className={`max-w-[80%] rounded-xl p-3 ${
                      message.type === 'user'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100'
                    } ${
                      (!message.content || !message.content.trim()) &&
                      message.type === 'assistant'
                        ? 'min-w-[300px]'
                        : ''
                    }`}
                  >
                    {message.content && message.content.trim() ? (
                      message.type === 'assistant' ? (
                        <div className='prose-sm prose max-w-none text-sm'>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              p: ({ children }) => (
                                <p className='mb-2 last:mb-0'>{children}</p>
                              ),
                              ul: ({ children }) => (
                                <ul className='mb-2 list-disc pl-4'>
                                  {children}
                                </ul>
                              ),
                              ol: ({ children }) => (
                                <ol className='mb-2 list-decimal pl-4'>
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className='mb-1'>{children}</li>
                              ),
                              code: ({ children }) => (
                                <code className='rounded bg-gray-200 px-1 py-0.5 font-mono text-xs'>
                                  {children}
                                </code>
                              ),
                              pre: ({ children }) => (
                                <pre className='overflow-x-auto rounded bg-gray-100 p-2 text-xs'>
                                  {children}
                                </pre>
                              ),
                              blockquote: ({ children }) => (
                                <blockquote className='border-l-4 border-gray-300 pl-3 italic'>
                                  {children}
                                </blockquote>
                              ),
                              h1: ({ children }) => (
                                <h1 className='mb-2 text-lg font-bold'>
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <h2 className='mb-2 text-base font-bold'>
                                  {children}
                                </h2>
                              ),
                              h3: ({ children }) => (
                                <h3 className='mb-1 text-sm font-bold'>
                                  {children}
                                </h3>
                              ),
                              strong: ({ children }) => (
                                <strong className='font-bold'>
                                  {children}
                                </strong>
                              ),
                              em: ({ children }) => (
                                <em className='font-bold text-gray-900'>
                                  {children}
                                </em>
                              ),
                              a: ({ href, children }) => (
                                <a
                                  href={href}
                                  className='text-primary hover:underline'
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  {children}
                                </a>
                              ),
                              table: ({ children }) => (
                                <table className='mb-2 border-collapse border border-gray-300 text-xs'>
                                  {children}
                                </table>
                              ),
                              thead: ({ children }) => (
                                <thead className='bg-gray-50'>{children}</thead>
                              ),
                              tbody: ({ children }) => (
                                <tbody>{children}</tbody>
                              ),
                              tr: ({ children }) => (
                                <tr className='border border-gray-300'>
                                  {children}
                                </tr>
                              ),
                              td: ({ children }) => (
                                <td className='border border-gray-300 px-2 py-1'>
                                  {children}
                                </td>
                              ),
                              th: ({ children }) => (
                                <th className='border border-gray-300 px-2 py-1 font-bold'>
                                  {children}
                                </th>
                              ),
                            }}
                          >
                            {message.content
                              // Convert literal \n to actual newlines
                              .replace(/\\n/g, '\n')
                              // Convert special link format to markdown links
                              .replace(/<([^|>]+)\|([^>]+)>/g, '[$2]($1)')
                              // Ensure proper spacing between consecutive links
                              .replace(/(\]\([^)]+\))\n(\[)/g, '$1\n\n$2')
                              // Ensure bullet points have proper spacing
                              .replace(/\n•/g, '\n\n•')
                              // Handle bullet points that might be at the start
                              .replace(/^•/g, '\n•')}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p className='whitespace-pre-wrap text-sm'>
                          {message.content}
                        </p>
                      )
                    ) : message.type === 'assistant' ? (
                      <div className='space-y-2'>
                        <Skeleton className='h-4 w-full rounded-lg' />
                        <Skeleton className='h-3 w-4/5 rounded-lg' />
                        <Skeleton className='h-3 w-3/4 rounded-lg' />
                        <Skeleton className='h-3 w-2/3 rounded-lg' />
                      </div>
                    ) : (
                      <div className='space-y-2'>
                        <Skeleton className='h-3 w-3/4 rounded-lg' />
                        <Skeleton className='h-3 w-1/2 rounded-lg' />
                      </div>
                    )}
                  </div>

                  {message.type === 'user' && (
                    <Avatar
                      size='sm'
                      src={
                        session?.user?.employee?.image ||
                        session?.user?.employee?.thumb ||
                        session?.user?.picture
                      }
                      className='flex-shrink-0'
                    />
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </ScrollShadow>
        </ModalBody>

        <ModalFooter className='px-6 pb-6 pt-4'>
          <form
            onSubmit={handleSubmit}
            className='flex w-full items-center gap-2'
          >
            <div className='relative flex-1'>
              <Input
                placeholder='Ask a follow-up question...'
                value={inputValue}
                onValueChange={setInputValue}
                size='md'
                variant='bordered'
                radius='full'
                isDisabled={isStreaming}
                classNames={{
                  inputWrapper: 'shadow-none bg-white border',
                  input: 'text-sm',
                }}
                endContent={
                  <Button
                    isIconOnly
                    variant='light'
                    size='sm'
                    type='submit'
                    isDisabled={!inputValue.trim() || isStreaming}
                    isLoading={isStreaming}
                    className='flex-shrink-0'
                  >
                    <FaPaperPlane size={14} />
                  </Button>
                }
              />
            </div>
          </form>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
